import React from 'react';
import noop from 'lodash/noop';
import confirm from 'utils/confirm';
import {unsavedMessage} from 'utils/const';
import Dialog from '@material-ui/core/Dialog';
import {ClosableDialogTitle} from '@components/dialog/closable-title/ClosableDialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import UserAvatar from 'components/pure/utils/UserAvatar';
import DatePicker from 'components/pure/utils/form/DatePicker';
import moment from 'moment';
import TimeField from 'components/pure/utils/form/TimeField';
import Time from 'records/Time';
import MeetingTypeSelector from 'components/coaching/MeetingTypeSelector';
import './ModifyReviewDialog.styl';
import {UserSelector} from 'components/pure/utils/form/UserSelector';
import {validate} from 'records/MeetingType';
import Loading from 'components/pure/utils/Loading';
import ReviewTypeDropdown from 'components/dashboard/common/ReviewTypeDropdown';
import SelectizeLookingDropdownArrow from 'components/utils/SelectizeLookingDropdownArrow';
import {CoachingReviewTypeDisplay} from 'components/dashboard/common/CoachingReviewTypeDisplayAndChooser';
import {displayName} from 'records/User';
import {FindTimeContainer} from '@components/calendar/FindTimeContainer';
import {DateOptionsArr} from '@store/@types/util/time/DayOfWeek';

export default class ModifyReviewDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onSaveAndClose = this.onSaveAndClose.bind(this);
    this.onCancel = this.onCancel.bind(this);

    this.state = {
      review: undefined,
      showErrors: false,
      submitting: false,
      errors: {},
    };
  }

  componentDidMount() {
    this.setState({review: this.props.review});
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!this.props.review) return;
    if (this.props.review.equals(prevProps.review)) return;

    this.setState({review: this.props.review});
  }

  onChange(review) {
    this.setState({review});
  }

  onSaveAndClose() {
    const {review} = this.state;

    // validate before submitting
    const errors = validate(review.meetingType);
    const showErrors = Object.keys(errors).length !== 0;
    this.setState({errors, showErrors});

    if (!showErrors) {
      this.setState({submitting: true});
      this.props.updateReview(review).then(() => {
        this.props.onRequestClose();
        this.setState({submitting: false});
      });
    }
  }

  onCancel() {
    // has the user changed the review since it was passed in?
    if (this.state.review && !this.state.review.equals(this.props.review)) {
      confirm(unsavedMessage).then(() => {
        this.setState({review: this.props.review});
        this.props.onRequestClose();
      }, noop);
    } else {
      this.props.onRequestClose();
    }
  }

  changeTime(time) {
    const newTime = this.state.review.scheduleAt.clone().hour(time.hour).minute(time.minute);

    this.setState({
      review: this.state.review.set('scheduleAt', newTime),
    });
  }

  changeDate(date) {
    const {scheduleAt: time} = this.state.review;
    const newTime = date.clone().hour(time.hour()).minute(time.minute());

    this.setState({
      review: this.state.review.set('scheduleAt', newTime),
    });
  }

  changeCoach = coachUserId => {
    this.setState({
      review: this.state.review.set('coachUserId', coachUserId),
    });
  };

  onChangeReviewType(reviewTypeId) {
    const {review} = this.state;
    this.setState({
      review: review.set('reviewTypeId', reviewTypeId),
    });
  }

  renderReviewTypes(selectionDisabled, review, availableReviewTypes) {
    const selectedReviewType = availableReviewTypes.find(
      reviewType => reviewType.id === review.reviewTypeId
    );
    const showDropdown =
      !review.recipientHasAnsweredQuestions && availableReviewTypes.size > 1 && !selectionDisabled;
    return (
      <FieldContainer label="Coaching Meeting Type" className="review-type">
        <div className="review-types">
          {showDropdown && (
            <ReviewTypeDropdown
              coachingReviewTypes={availableReviewTypes}
              value={review.reviewTypeId}
              onSelect={({id}) =>
                this.setState({
                  review: review.set('reviewTypeId', id),
                })
              }
              width={295}
              arrow={(open, onShowDropdown) => (
                <SelectizeLookingDropdownArrow open={open} onShowDropdown={onShowDropdown} />
              )}
            />
          )}
          {!showDropdown && (
            <CoachingReviewTypeDisplay briefLabel reviewType={selectedReviewType} />
          )}
        </div>
      </FieldContainer>
    );
  }

  renderDialogContent() {
    const {errors, review, showErrors} = this.state;
    if (!review) {
      return null;
    }

    const {scheduleAt: timestamp, meetingType, coachUserId} = this.state.review;
    const {coaches, selectionDisabled, plan, reviewTypes, reviewTypesLoaded} = this.props;

    if (!plan || !plan.recipient || !reviewTypesLoaded) {
      return <Loading className="content-loading" />;
    }

    const availableReviewTypes = reviewTypes.filter(reviewType =>
      plan.reviewTypeIds.has(reviewType.id)
    );

    const findTimeChoiceMade = chosenTime => {
      // Create moment from chosenTime assuming day chosen
      // would be next day from current scheduleAt if different
      // than current schedule at.
      const currentScheduledDateTime = this.state.review.scheduleAt;
      const currentScheduledDayIndex = DateOptionsArr.findIndex(
        x => x.label === currentScheduledDateTime.format('dddd')
      );
      const chosenDayIndex = DateOptionsArr.findIndex(x => x.label === chosenTime.day);
      const offset =
        chosenDayIndex >= currentScheduledDayIndex
          ? chosenDayIndex - currentScheduledDayIndex
          : chosenDayIndex + 7 - currentScheduledDayIndex;

      const newDateTime = currentScheduledDateTime
        .clone()
        .add(offset, 'd')
        .hour(chosenTime.hour)
        .minute(chosenTime.minutes);

      this.setState({
        review: this.state.review.set('scheduleAt', newDateTime),
      });
    };

    return (
      <div className="modify-review-dialog-container">
        <section>
          <FieldContainer label="Meeting Time" labelType="div" className="meeting-time">
            <DatePicker
              value={timestamp}
              minDate={moment()}
              hintText=" / / "
              onChange={value => this.changeDate(value)}
            />
            <span className="spacer">at</span>
            <TimeField
              value={new Time({hour: timestamp.hour(), minute: timestamp.minute()})}
              onChange={value => this.changeTime(value)}
            />
            <FindTimeContainer
              coachUserId={plan?.coach?.id}
              recipientUserId={plan?.recipient?.id}
              recipientFirstName={plan.recipient.info.firstName}
              meetingMinutes={plan.schedule.durationInMinutes}
              searchWeeks={1 /* Only finding times for single meeting/review */}
              dayOfWeek={review.scheduleAt.format('dddd')}
              desiredTime={review.scheduleAt.format('HH:mm')}
              startDate={review.scheduleAt}
              findTimeChoiceMade={findTimeChoiceMade}
            />
          </FieldContainer>
        </section>
        <section>{this.renderReviewTypes(selectionDisabled, review, availableReviewTypes)}</section>
        {/* Not to be confused with review types above, which we call meeting types in the UI. */}
        <section>
          <FieldContainer label="How Will You Meet?" labelType="div" className="meeting-type">
            <MeetingTypeSelector
              meetingType={meetingType}
              onChange={meetingType =>
                this.setState({
                  review: review.set('meetingType', meetingType),
                })
              }
              showLocationLabels={false}
              errors={errors}
              showErrors={showErrors}
            />
          </FieldContainer>
        </section>
        <section>
          <FieldContainer labelType="div" label="Coach" className="coach">
            <UserSelector
              value={coachUserId}
              users={coaches.delete(plan.recipient.id)}
              sessionUser={this.props.sessionUser}
              onChange={this.changeCoach}
            />
          </FieldContainer>
        </section>
      </div>
    );
  }

  render() {
    const {sessionUser, open = false, plan} = this.props;
    const {submitting} = this.state;

    // Loading state for falsy plan is in renderDialogContent().
    const notRep = !plan ? false : sessionUser.id !== plan.recipient.id;

    // Avoid passing false instead of a function, which causes propTypes errors and other nonsense.
    const whenNotSubmitting = fun => (submitting ? null : fun);

    const title = plan ? (
      <div className="modify-review-dialog-title">
        <UserAvatar user={plan.recipient.info} />
        <div className="modify-review-dialog-title-text">
          Modify Meeting with{' '}
          {notRep ? displayName(plan.recipient.info) : displayName(plan.coach.info)}
        </div>
      </div>
    ) : (
      'Modify Meeting'
    );

    return (
      <Dialog
        className="modify-review-dialog"
        open={open}
        title={title}
        maxWidth={false}
        onRequestClose={this.onCancel}>
        <ClosableDialogTitle onClose={this.onCancel}>{title}</ClosableDialogTitle>
        <DialogContent>{this.renderDialogContent(!notRep)}</DialogContent>
        <DialogActions>
          <Button variant="outlined" className="ev-button" onClick={this.onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="ev-button"
            disabled={submitting}
            onClick={whenNotSubmitting(this.onSaveAndClose)}>
            {submitting ? 'Saving...' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
